import type { ShoppingList } from '@recipes/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { ServingsStepper } from './servings-stepper';

export interface AddToShoppingDialogProps {
  recipeId: string;
  defaultServings: string;
  servingsLabel?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NEW_LIST = '__new__';

/** Minimal "add recipe to shopping list" flow (POST /shopping-lists/:id/recipes). */
export function AddToShoppingDialog({
  recipeId,
  defaultServings,
  servingsLabel,
  open,
  onOpenChange,
}: AddToShoppingDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const lists = useQuery({
    queryKey: queryKeys.shopping.all,
    queryFn: () => api.shopping.list({ status: 'ACTIVE' }),
    enabled: open,
    retry: false,
  });
  const [listId, setListId] = useState<string>(NEW_LIST);
  const [newName, setNewName] = useState('');
  const [servings, setServings] = useState(defaultServings);
  const [includeOptional, setIncludeOptional] = useState(false);
  const [subtractPantry, setSubtractPantry] = useState(false);

  useEffect(() => {
    if (open) setServings(defaultServings);
  }, [open, defaultServings]);
  useEffect(() => {
    if (lists.data && lists.data.length > 0 && listId === NEW_LIST && newName === '')
      setListId(lists.data[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists.data]);

  const add = useMutation({
    mutationFn: async () => {
      let target: ShoppingList | undefined = lists.data?.find((l) => l.id === listId);
      if (!target || listId === NEW_LIST) {
        target = await api.shopping.create({
          name: newName.trim() || t('recipes.shopping.defaultListName'),
        });
      }
      return api.shopping.addRecipe(target.id, {
        recipeId,
        servings,
        includeOptional,
        subtractPantry,
      });
    },
    onSuccess: (detail) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shopping.all });
      toast.show({
        title: t('recipes.shopping.added', { name: detail.name }),
        variant: 'success',
      });
      onOpenChange(false);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="add-to-shopping-dialog">
        <DialogHeader>
          <DialogTitle>{t('recipes.shopping.title')}</DialogTitle>
          <DialogDescription>{t('recipes.shopping.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div>
            <Label htmlFor="shopping-list" className="mb-1 block">
              {t('recipes.shopping.list')}
            </Label>
            <Select value={listId} onValueChange={setListId}>
              <SelectTrigger id="shopping-list">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lists.data?.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} ({l.itemCount})
                  </SelectItem>
                ))}
                <SelectItem value={NEW_LIST}>{t('recipes.shopping.newList')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {listId === NEW_LIST && (
            <div>
              <Label htmlFor="shopping-new-name" className="mb-1 block">
                {t('recipes.shopping.newListName')}
              </Label>
              <Input
                id="shopping-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={120}
              />
            </div>
          )}
          <div>
            <Label className="mb-1 block">{t('recipes.shopping.servings')}</Label>
            <ServingsStepper value={servings} onChange={setServings} label={servingsLabel} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="shopping-optional"
              checked={includeOptional}
              onCheckedChange={(v) => setIncludeOptional(v === true)}
            />
            <Label htmlFor="shopping-optional">{t('recipes.shopping.includeOptional')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="shopping-pantry"
              checked={subtractPantry}
              onCheckedChange={(v) => setSubtractPantry(v === true)}
            />
            <Label htmlFor="shopping-pantry">{t('recipes.shopping.subtractPantry')}</Label>
          </div>
          <DialogFooter>
            <Button asChild variant="ghost">
              <Link to="/shopping">{t('nav.shopping')}</Link>
            </Button>
            <Button type="submit" loading={add.isPending} data-testid="add-to-shopping-submit">
              {t('recipes.shopping.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
